<template>
  <div class="tasks-page">
    <!-- Page Header -->
    <div class="tasks-page__header">
      <div class="tasks-page__title">
        <theme-icon icon="task" type="icons" />
        <h1>Tasks</h1>
      </div>
      <div class="tasks-page__actions">
        <themed-button @click="handleCreateTask">
          <theme-icon icon="plus" type="icons" />
          <span>Create Task</span>
        </themed-button>
      </div>
    </div>

    <!-- Tab Navigation -->
    <themed-tabs
      v-model="activeTab"
      :tabs="tabs"
      @tab-change="handleTabChange"
    >
      <template #default="{ activeTab: currentTab }">
        <!-- Dashboard View -->
        <task-dashboard
          v-if="currentTab === 'dashboard'"
          @create-task="handleCreateTask"
          @edit-task="handleEditTask"
        />

        <!-- Kanban View -->
        <task-kanban
          v-else-if="currentTab === 'kanban'"
          :visible-fields="visibleKanbanFields"
          @create-task="handleCreateTask"
          @edit-task="handleEditTask"
        />

        <!-- Calendar View -->
        <task-calendar
          v-else-if="currentTab === 'calendar'"
          @create-task="handleCreateTask"
          @edit-task="handleEditTask"
        />
      </template>
    </themed-tabs>

    <!-- Task Form Modal -->
    <themed-modal
      v-if="showTaskModal"
      name="task-form-modal"
      :title="isEditMode ? 'Task Details' : 'Create Task'"
      size="lg"
      @close="closeTaskModal"
    >
      <!-- Detail view for existing tasks -->
      <task-detail
        v-if="isEditMode"
        ref="taskDetail"
        :task="selectedTask"
        @save="handleTaskSaved"
        @close="closeTaskModal"
        @open-task="handleOpenSubtask"
        @refresh="refreshSelectedTask"
        @loading-change="taskFormLoading = $event"
      />

      <!-- Create form for new tasks -->
      <task-form
        v-else
        ref="taskForm"
        :task="selectedTask"
        :project-id="prefilledProjectId"
        :site-id="prefilledSiteId"
        @save="handleTaskSaved"
        @cancel="closeTaskModal"
        @loading-change="taskFormLoading = $event"
        @loading-options-change="formLoading = $event"
      />
      
      <template #footer>
        <div class="modal-actions">
          <themed-button
            variant="secondary"
            @click="closeTaskModal"
            :disabled="taskFormLoading || formLoading"
          >
            Cancel
          </themed-button>
          <themed-button
            @click="submitTaskForm"
            :disabled="taskFormLoading || formLoading"
          >
            <theme-icon v-if="taskFormLoading || formLoading" icon="loading" type="icons" class="w-4 h-4 animate-spin" />
            <span v-else>{{ isEditMode ? 'Update Task' : 'Create Task' }}</span>
          </themed-button>
        </div>
      </template>
    </themed-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ThemedTabs from '../ThemedTabs.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedModal from '../ThemedModal.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TaskForm from './TaskForm.vue';
import TaskDetail from './TaskDetail.vue';
import TaskDashboard from './TaskDashboard.vue';
import TaskKanban from './TaskKanban.vue';
import TaskCalendar from './TaskCalendar.vue';

export default {
  name: 'TasksPage',

  components: {
    ThemedTabs,
    ThemedButton,
    ThemedModal,
    ThemeIcon,
    TaskForm,
    TaskDetail,
    TaskDashboard,
    TaskKanban,
    TaskCalendar
  },

  props: {
    openTaskId: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      activeTab: 'dashboard',
      showTaskModal: false,
      selectedTask: null,
      prefilledProjectId: null,
      prefilledSiteId: null,
      taskFormLoading: false,
      formLoading: false,
      visibleKanbanFields: []
    };
  },

  computed: {
    ...mapState('tasks', ['tasks', 'loading']),
    ...mapGetters('tasks', ['activeStatuses', 'activePriorities']),

    tabs() {
      return [
        {
          label: 'Dashboard',
          value: 'dashboard'
        },
        {
          label: 'Kanban',
          value: 'kanban'
        },
        {
          label: 'Calendar',
          value: 'calendar'
        }
      ];
    },

    isEditMode() {
      return this.selectedTask && this.selectedTask.id;
    }
  },

  async mounted() {
    // Initialize Vuex store data
    await this.initializeData();
    // Load visible fields from localStorage
    this.loadVisibleKanbanFields();

    // Auto-open task if openTaskId is provided (shareable link)
    if (this.openTaskId) {
      try {
        const response = await axios.get(`/api/tasks/${this.openTaskId}`);
        this.handleEditTask(response.data);
      } catch (error) {
        console.error('Error loading task from shareable link:', error);
      }
    }
  },

  methods: {
    async initializeData() {
      try {
        // Fetch statuses and priorities for the entire module
        await Promise.all([
          this.$store.dispatch('tasks/fetchStatuses'),
          this.$store.dispatch('tasks/fetchPriorities')
        ]);
      } catch (error) {
        console.error('Error initializing tasks data:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load task data. Please refresh the page.'
        });
      }
    },

    handleTabChange(tabValue) {
      // Tab change handler - can be used for analytics or additional logic
      console.log('Tab changed to:', tabValue);
    },

    handleCreateTask(options = {}) {
      this.selectedTask = null;
      this.prefilledProjectId = options.projectId || null;
      this.prefilledSiteId = options.siteId || null;
      
      // Handle status_id from Kanban
      if (options.statusId) {
        this.selectedTask = { status_id: options.statusId };
      }
      
      // Handle due_date from Calendar
      if (options.dueDate) {
        const formattedDate = this.formatDateForInput(options.dueDate);
        this.selectedTask = { 
          ...(this.selectedTask || {}),
          due_date: formattedDate
        };
      }
      
      this.showTaskModal = true;
      this.$nextTick(() => {
        this.$modal.show('task-form-modal');
      });
    },

    formatDateForInput(date) {
      if (!date) return '';
      
      try {
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) return '';
        
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    },

    handleEditTask(task) {
      this.selectedTask = task;
      this.prefilledProjectId = null;
      this.prefilledSiteId = null;
      this.showTaskModal = true;
      this.$nextTick(() => {
        this.$modal.show('task-form-modal');
      });
    },

    async handleTaskSaved(task) {
      // Refresh the current view's data
      await this.$store.dispatch('tasks/fetchTasks');
      
      // Close the modal
      this.closeTaskModal();

      // Show success notification (already handled in TaskForm, but can add here if needed)
    },

    submitTaskForm() {
      if (this.$refs.taskDetail) {
        this.$refs.taskDetail.submitForm();
      } else if (this.$refs.taskForm) {
        this.$refs.taskForm.handleSubmit();
      }
    },

    handleOpenSubtask(subtask) {
      // Add a brief loading state to prevent jarring transition
      this.formLoading = true;
      this.selectedTask = null;
      
      // Use nextTick to ensure the form is fully unmounted before loading new task
      this.$nextTick(() => {
        this.selectedTask = subtask;
        // formLoading will be set to false by TaskForm when it finishes loading
      });
    },

    async refreshSelectedTask() {
      if (!this.selectedTask || !this.selectedTask.id) return;
      try {
        const response = await axios.get(`/api/tasks/${this.selectedTask.id}`);
        this.selectedTask = response.data;
      } catch (error) {
        console.error('Error refreshing task:', error);
      }
    },

    closeTaskModal() {
      this.$modal.hide('task-form-modal');
      this.showTaskModal = false;
      this.selectedTask = null;
      this.prefilledProjectId = null;
      this.prefilledSiteId = null;
      this.taskFormLoading = false;
      this.formLoading = false;
    },
    
    loadVisibleKanbanFields() {
      // Load from the same localStorage key as TaskCards for consistency
      const savedColumns = localStorage.getItem('tasks_table_visible_columns');
      
      if (savedColumns) {
        try {
          const parsed = JSON.parse(savedColumns);
          if (Array.isArray(parsed)) {
            this.visibleKanbanFields = parsed;
          } else {
            this.visibleKanbanFields = this.getDefaultVisibleFields();
          }
        } catch (e) {
          console.error('Failed to parse saved column preferences:', e);
          this.visibleKanbanFields = this.getDefaultVisibleFields();
        }
      } else {
        this.visibleKanbanFields = this.getDefaultVisibleFields();
      }
    },
    
    getDefaultVisibleFields() {
      // Default visible fields for Kanban cards
      return [
        'title',
        'assignee.name',
        'status.name',
        'priority.name',
        'due_date'
      ];
    }
  }
};
</script>
